/**
 * Graph Health Tile — operational visibility for Oracle Knowledge Graph
 * auto-refresh.
 *
 * Answers: "Is the intelligence network current?"
 *
 * Reads /api/oracle/graph/status (admin-gated). Renders last refresh,
 * refresh count, dossier/entity/redirect counts, and a health dot.
 * Force-refresh button calls /api/oracle/graph/refresh.
 *
 * Small and unobtrusive — one tile inside the existing Command Center
 * grid. Zero new backend work; the endpoints already exist.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Network, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { oracleGraphRefresh, oracleGraphStatus } from "../../lib/api";

const relTime = (iso) => {
  if (!iso) return "never";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
};

// Health state resolver — never mixes signals; error > paused > pending > healthy.
const resolveHealth = (s) => {
  if (!s) return { label: "LOADING",  color: "#757065", tone: "load" };
  if (s.last_error) return { label: "ERROR",   color: "#D92D20", tone: "error" };
  if (s.paused)     return { label: "PAUSED",  color: "#A39E93", tone: "paused" };
  if (s.pending)    return { label: "PENDING", color: "#E0A526", tone: "pending" };
  return              { label: "HEALTHY", color: "#5DD4B3", tone: "ok" };
};

export const GraphHealthTile = ({ opToken, userToken, testid = "cmd-graph-health" }) => {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr]         = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const s = await oracleGraphStatus(opToken, userToken);
      setStatus(s);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "failed to load");
    } finally {
      setLoading(false);
    }
  }, [opToken, userToken]);

  useEffect(() => { load(); }, [load]);

  const onForce = async () => {
    setRefreshing(true);
    try {
      await oracleGraphRefresh(opToken, userToken);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const health = resolveHealth(status);

  return (
    <div
      data-testid={testid}
      className="border border-[#2D2A26] bg-[#0F0F0F] p-4 flex flex-col gap-3"
    >
      {/* Header row — icon, label, health pill */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Network size={11} className="text-[#5DD4B3] shrink-0" />
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3]">
            Oracle Graph · Auto-refresh
          </span>
        </div>
        <span
          data-testid={`${testid}-status`}
          className="font-mono text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 border shrink-0"
          style={{ color: health.color, borderColor: `${health.color}66` }}
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
            style={{ backgroundColor: health.color, boxShadow: `0 0 6px ${health.color}` }}
          />
          {health.label}
        </span>
      </div>

      {loading ? (
        <div className="font-mono text-[10px] text-[#757065] italic">
          <Loader2 size={10} className="inline animate-spin mr-1.5" />
          reading graph state…
        </div>
      ) : (
        <>
          {/* Freshness + refresh count */}
          <div className="flex items-baseline justify-between font-mono text-[10px]">
            <span className="text-[#A39E93]">Last refresh</span>
            <span
              data-testid={`${testid}-last-refresh`}
              className="text-[#E4DFD3]"
              title={status?.last_refresh_at || ""}
            >
              {relTime(status?.last_refresh_at)}
              {typeof status?.last_duration_ms === "number" && (
                <span className="text-[#757065] ml-1.5">
                  ({Math.round(status.last_duration_ms)} ms)
                </span>
              )}
            </span>
          </div>
          <div className="flex items-baseline justify-between font-mono text-[10px]">
            <span className="text-[#A39E93]">Refresh count</span>
            <span data-testid={`${testid}-refresh-count`} className="text-[#E4DFD3]">
              {status?.refresh_count ?? 0}
            </span>
          </div>

          {/* Corpus counts — one row */}
          <div className="grid grid-cols-3 gap-2 pt-1 border-t border-[#2D2A26]">
            <Count label="Dossiers"  value={status?.dossier_count}  testid={`${testid}-dossiers`} />
            <Count label="Entities"  value={status?.entity_count}   testid={`${testid}-entities`} />
            <Count label="Redirects" value={status?.redirect_count} testid={`${testid}-redirects`} />
          </div>

          {/* Error banner + force-refresh */}
          {status?.last_error && (
            <div
              data-testid={`${testid}-error`}
              className="mt-1 px-2 py-1.5 border border-[#D92D20]/60 bg-[#1a0808] flex items-start gap-1.5"
            >
              <AlertTriangle size={10} className="text-[#D92D20] mt-0.5 shrink-0" />
              <span className="font-mono text-[9px] text-[#E4DFD3] break-words">
                {status.last_error}
              </span>
            </div>
          )}
          {err && (
            <div className="font-mono text-[9px] text-[#D92D20]" data-testid={`${testid}-fetch-error`}>
              {err}
            </div>
          )}

          {/* Force refresh button — always shown, prominent when error */}
          <button
            data-testid={`${testid}-force-refresh`}
            onClick={onForce}
            disabled={refreshing || status?.pending}
            className={
              "self-start inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.25em] " +
              "disabled:opacity-50 disabled:cursor-not-allowed " +
              (status?.last_error
                ? "border-[#D92D20] text-[#D92D20] hover:bg-[#1a0808]"
                : "border-[#454038] text-[#A39E93] hover:border-[#5DD4B3] hover:text-[#E4DFD3]")
            }
          >
            {refreshing
              ? <Loader2 size={10} className="animate-spin" />
              : <RefreshCw size={10} />}
            Force refresh
          </button>
        </>
      )}
    </div>
  );
};

const Count = ({ label, value, testid }) => (
  <div data-testid={testid} className="text-center">
    <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#757065]">
      {label}
    </div>
    <div className="font-display text-lg text-[#E4DFD3] leading-tight">
      {typeof value === "number" ? value.toLocaleString() : "—"}
    </div>
  </div>
);

export default GraphHealthTile;
